'use client';

import type {
  Benutzer,
  Rolle,
} from '@/lib/api/auth-types';
import { istSeitenFeatureAktiv, type SeitenFeatureFlags } from '@/lib/feature-flags';
import seitenRegistryRoh from '../../../shared/page-registry.json';

type SeitenRegistryEintrag = {
  page_key: string;
  label: string;
  beschreibung: string;
  primary_path: string;
  path_aliases: string[];
  default_roles: Rolle[];
  customizable: boolean;
  startseite_prioritaet: number;
};

const SEITEN_REGISTRY = seitenRegistryRoh as SeitenRegistryEintrag[];

const STANDARD_STARTSEITEN: Record<Rolle, string> = {
  admin: '/steuerung',
  einkauf: '/auftragsbestaetigung',
  qualitaet: '/messberichte',
  logistik: '/lieferungen',
};

const PFAD_ZU_PAGE_KEY = new Map<string, string>();
for (const seite of SEITEN_REGISTRY) {
  PFAD_ZU_PAGE_KEY.set(seite.primary_path, seite.page_key);
  for (const alias of seite.path_aliases) {
    PFAD_ZU_PAGE_KEY.set(alias, seite.page_key);
  }
}

function basisPfadAusPfad(pfad: string): string {
  const segmente = pfad.split('/').filter(Boolean);
  return segmente.length > 0 ? `/${segmente[0]}` : '/';
}

export function pageKeyFuerPfad(pfad: string): string | null {
  return PFAD_ZU_PAGE_KEY.get(basisPfadAusPfad(pfad)) ?? null;
}

// Seiten, die nur sichtbar sein duerfen, wenn der aktuell konfigurierte
// ERP-Adapter (adapter.erp, siehe StatusAntwort.config_zusammenfassung.adapter.erp)
// exakt dem hier hinterlegten Wert entspricht. erp-test (Fixture-Testdaten) und
// erp (Live-SQL-Server) sind sonst nicht sinnvoll nutzbar bzw. der zugehoerige
// Backend-Endpoint lehnt mit 409 ab.
const ERP_ADAPTER_ANFORDERUNG: Partial<Record<string, string>> = {
  'erp-test': 'winform-test',
  erp: 'winform',
};

function erfuelltErpAdapterAnforderung(
  pageKey: string,
  erpAdapterTyp?: string | null,
): boolean {
  const erforderlich = ERP_ADAPTER_ANFORDERUNG[pageKey];
  if (!erforderlich) return true;
  return (erpAdapterTyp ?? '') === erforderlich;
}

export function effektiveSeitenrechte(
  benutzer: Benutzer | null | undefined,
  seitenFeatureFlags?: SeitenFeatureFlags | null,
  erpAdapterTyp?: string | null,
): string[] {
  if (!benutzer) return [];
  let rechte: string[];

  if (benutzer.rolle === 'admin') {
    rechte = SEITEN_REGISTRY.map((seite) => seite.page_key);
  } else if (Array.isArray(benutzer.seitenrechte) && benutzer.seitenrechte.length > 0) {
    rechte = benutzer.seitenrechte;
  } else {
    rechte = SEITEN_REGISTRY
      .filter((seite) => benutzer.rolle === 'admin' || seite.default_roles.includes(benutzer.rolle))
      .map((seite) => seite.page_key);
  }

  return rechte.filter(
    (pageKey) =>
      istSeitenFeatureAktiv(pageKey, seitenFeatureFlags) &&
      erfuelltErpAdapterAnforderung(pageKey, erpAdapterTyp),
  );
}

export function hatZugriffAufSeite(
  benutzer: Benutzer | null | undefined,
  pfad: string,
  seitenFeatureFlags?: SeitenFeatureFlags | null,
  erpAdapterTyp?: string | null,
): boolean {
  if (!benutzer) return false;
  const pageKey = pageKeyFuerPfad(pfad);
  if (!pageKey) return false;
  if (!istSeitenFeatureAktiv(pageKey, seitenFeatureFlags)) {
    return false;
  }
  if (!erfuelltErpAdapterAnforderung(pageKey, erpAdapterTyp)) {
    return false;
  }
  return effektiveSeitenrechte(benutzer, seitenFeatureFlags, erpAdapterTyp).includes(pageKey);
}

export function startseiteFuerBenutzer(
  benutzer: Benutzer | null | undefined,
  seitenFeatureFlags?: SeitenFeatureFlags | null,
  erpAdapterTyp?: string | null,
): string {
  if (!benutzer) return '/login';

  const standardStartseite = STANDARD_STARTSEITEN[benutzer.rolle];
  if (
    standardStartseite &&
    hatZugriffAufSeite(benutzer, standardStartseite, seitenFeatureFlags, erpAdapterTyp)
  ) {
    return standardStartseite;
  }

  const rechte = new Set(effektiveSeitenrechte(benutzer, seitenFeatureFlags, erpAdapterTyp));
  for (const seite of SEITEN_REGISTRY) {
    if (rechte.has(seite.page_key)) {
      return seite.primary_path;
    }
  }

  return '/login';
}